import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import flatMap from 'lodash/flatMap';
import pick from 'lodash/pick';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spinner, useToast } from '@chakra-ui/react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { getBaBSingleProducts, getBundleDiscounts } from '../../bundles/api';
import { getIdFromGid, transformProductsInBundle } from '../utils';
import { useBundleSetting } from '../state/BundleSettingContext';
import {
  getCurrencyFormatter,
  getGidFromIdForResource,
  calculateDiscount,
  isGlobalProductList,
  splitProductInVariants,
  notEmpty,
} from '../../utils';

import { useNotifs } from '../../utils/useNotif';

import {
  extractVariantAndImage,
  getOnlySelectedVariants,
  getDiscount,
  getPricingPolicyCycleDiscount,
  formatLiquidProductToGlobalProduct,
} from '../../utils/BundleUtils';
import { useCreateBundle } from '../../bundles/CreateBundle/useCreateBundle';

import Button from '../../components/Button';
import { Span } from '../../components/Typography';
import { ProductsHandler } from '../../components/Bundle/CreateBundleContent';
import { BundleSelector } from '../../components/Bundle/BundleSizeSelector';
import SlidePanel from '../../components/SlidePanel';
import ProgressBar from '../../bundles/CreateBundle/ProgressBar';
import { useControlSlidePanel } from '../../components/SlidePanel/useControlSlidePanel';
import { VariantStyle } from '../../../components/BundleSettings/types';
import {
  CreateBundleContainer,
  CreateBundleFooter,
  CreateBundleButtonInfo,
} from '../../components/Bundle/BundleBoxStructure';
import { filterProducts } from '../../bundles/bundleUtils';
import { FiltersSection } from '../../bundles/CreateBundle/FiltersSection';
import { useGlobalContext } from '../state/GlobalContext';
import { useBundleSectionsSelection } from '../../bundles/BabSections/useBabSections';
import { CreateBundleWithSections } from '../../bundles/BabSections/CreateBundleSections';
import {
  checkSectionsLimits,
  findSectionSettings,
} from '../../bundles/BabSections/utils';
import { removeOutOfStockVariants } from './utils';

const MAX_CHUNK_SIZE = 19;
const BODY_BAB_CLASS = 'awt-bab-open';
const BODY_BAB_EDIT_CLASS = 'awt-bab-edit-open';

const SpinnerContainer = styled.div.attrs({
  className: 'bundle-loading-container',
})`
  position: relative;
  padding: 100px 0;
`;
export interface BundleUpdateProps {
  bundle?: LiquidBundleParsed;
  product: Product;
  updateBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: Discount[],
    oneTimeAddon?: boolean,
  ) => void;
  closeModal: () => void;
  subscription: any;
  shopConfiguration?: ShopConfiguration;
  subscriber?: (cb: (data: any) => void) => () => void;
}

const BundleUpdate: React.FC<BundleUpdateProps> = ({
  bundle,
  product,
  closeModal,
  updateBundle,
  subscription,
  shopConfiguration,
  subscriber,
}) => {
  const pricingPolicy = getPricingPolicyCycleDiscount(
    product?.PricingPolicyCycleDiscounts,
    product?.SuccessfulBillingAttempts,
  );
  const discount = getDiscount(pricingPolicy);

  const [bundleData, setBundleData] = React.useState(bundle);

  const unsubscribe = subscriber((data) => {
    setBundleData(data);
  });

  useEffect(() => unsubscribe, [unsubscribe]);

  const productsInBundle = removeOutOfStockVariants(
    transformProductsInBundle(product.CustomAttributes),
    product?.BaBOutOfStockVariants,
    shopConfiguration?.ShopOversell,
  );

  React.useEffect(() => {
    document.body.classList.toggle(BODY_BAB_CLASS, true);
    document.body.classList.toggle(BODY_BAB_EDIT_CLASS, true);
    // send custom event so that merchants can listen for box edit modal open
    const event = new CustomEvent('awtomic-box-edit-modal-opened', {
      detail: bundleData,
    });
    window.dispatchEvent(event);

    return () => {
      document.body.classList.toggle(BODY_BAB_CLASS, false);
      document.body.classList.toggle(BODY_BAB_EDIT_CLASS, false);
      // send custom event so that merchants can listen for box edit modal close
      const event = new CustomEvent('awtomic-box-edit-modal-closed', {
        detail: bundleData,
      });
      window.dispatchEvent(event);
    };
  }, []);

  if (!bundleData) {
    return (
      <SpinnerContainer className="bundle-loading-container">
        <Spinner size="xl" style={{ margin: '0 auto', display: 'block' }} />
      </SpinnerContainer>
    );
  }

  return (
    <BundleUpdateContent
      subscription={subscription}
      variantId={product.VariantId}
      hasDuplicatedLines={product.hasDuplicatedLines}
      subscriptionId={product.PK}
      bundleData={bundleData}
      closeModal={closeModal}
      productsInBundle={productsInBundle}
      updateBundle={updateBundle}
      lineId={product.SubscriptionContractLineId}
      currencyCode={product.PricingPolicyBasePriceCurrencyCode}
      discount={discount}
      oneTimeAddon={product.OneTimeAddon}
      shopConfiguration={shopConfiguration}
    />
  );
};

export default BundleUpdate;

export type Discount = Partial<{
  DynamicDiscountAmount: string | number;
  DynamicDiscountCode: string;
  DynamicDiscountId: string;
  DynamicDiscountType: string;
}>;

interface BundleUpdateContentProps {
  variantId: string;
  subscriptionId: string;
  bundleData: LiquidBundleParsed;
  updateBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: Discount[],
    oneTimeAddon?: boolean,
  ) => void;
  lineId: string;
  closeModal: () => void;
  productsInBundle: Record<string, number>;
  currencyCode?: string;
  discount?: {
    type: string;
    value: number;
  };
  subscription: any;
  shopConfiguration?: ShopConfiguration;
  oneTimeAddon?: boolean;
  hasDuplicatedLines?: boolean;
}

const BundleUpdateContent = ({
  bundleData,
  lineId,
  closeModal,
  productsInBundle,
  updateBundle,
  variantId,
  currencyCode,
  discount,
  subscription,
  shopConfiguration,
  oneTimeAddon,
  hasDuplicatedLines,
}: BundleUpdateContentProps) => {
  const { t } = useTranslation();
  const { bundle } = bundleData;
  const toast = useToast();
  const isDynamic = bundle.Type === 'dynamicBaB';
  const isPrepaid = useMemo(() => subscription.HasPrepaidLines, [subscription]);

  const hasBabSections =
    bundle?.Sections &&
    isArray(bundle?.Sections) &&
    bundle?.Sections?.length >= 1;

  const {
    basePrice,
    bundlePrice,
    discountedPrice,
    productsLength,
    size,
    isSaveable,
    canAddProducts,
    hasDynamicSize,
    addProduct,
    removeProduct,
    selectedProducts,
    getItemProperties,
    getSelectedProductsWithPropsForCart,
    updateBoxSize,
    sellingPlan,
    initialSelectedProducts,
    variantId: selectedSetting,
    dynamicVariantId,
    setSelectedProducts,
    settings,
  } = useCreateBundle({
    hasDuplicatedLines,
    bundle,
    selectedDynamicVariantId: subscription.Lines.find(
      ({ SubscriptionContractLineId }) => SubscriptionContractLineId === lineId,
    ).CustomAttributes.find(({ key }) => key === '_variantId')?.value,
    productsInCollection: bundleData.productsInCollection,
    productsInAddonsCollection: bundleData.productsInAddonsCollection,
    initialSelectedProducts: productsInBundle,
    subscription,
    shopConfiguration,
    productBundle: bundleData.productBundle,
  });
  const {
    customerPortalSettings,
    hideBaBSoldOutOptions,
    ignoreBaBOutOfStockAllowance,
  } = useGlobalContext();
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const getAllowedBaBSwapVariants = useCallback(() => {
    const allowedBaBSwapVariants = subscription.Lines.find(
      ({ SubscriptionContractLineId }) => SubscriptionContractLineId === lineId,
    )?.allowedBaBSwapVariants;
    if (customerPortalSettings?.productChange === 'SAME_SUBSCRIPTION') {
      const newSettings = bundle.Settings.filter(({ VariantId }) =>
        allowedBaBSwapVariants
          ? allowedBaBSwapVariants?.includes(VariantId)
          : true,
      );

      return newSettings;
    }

    return bundle.Settings;
  }, [
    bundle.Settings,
    customerPortalSettings?.productChange,
    lineId,
    subscription.Lines,
  ]);

  const [filteredProducts, setFilteredProducts] = useState<
    GlobalVariant[] | GlobalProduct[]
  >([]);
  useEffect(() => {
    setFilteredProducts(bundleData.productsInCollection);
  }, [bundleData.productsInCollection]);

  useEffect(() => {
    const bundleId = getIdFromGid(bundleData?.bundle?.ProductId);
    if (bundleId) {
      const filtersBasedOnSettings =
        subscription.CustomAttributes?.find(
          ({ key }) => key === `_bundle_filters_${bundleId}`,
        )?.value?.split(',') || [];

      setSelectedFilters(filtersBasedOnSettings);
    }
  }, [bundleData?.bundle?.ProductId, subscription?.CustomAttributes]);

  useEffect(() => {
    const allFilters =
      bundle?.BuildABoxFilterGroups?.flatMap(({ Filters, Id }) =>
        Filters.map((filter) => ({ ...filter, groupId: Id })),
      ) || [];

    const selectedFiltersConfig = allFilters?.filter(({ Id }) =>
      selectedFilters.includes(Id),
    );

    const newProducts = filterProducts({
      selectedFilters: selectedFiltersConfig,
      products: bundleData.productsInCollection,
      variantStyle: bundle?.VariantStyle,
    });

    setFilteredProducts(
      sortProductsBySelected(newProducts, initialSelectedProducts),
    );
  }, [
    bundle?.BuildABoxFilterGroups,
    bundle?.VariantStyle,
    bundleData.productsInCollection,
    initialSelectedProducts,
    selectedFilters,
  ]);

  const handleResetFilters = (groupFilters) => {
    const groupFiltersIds = groupFilters.map(({ Id }) => Id);
    const newFilters = selectedFilters.filter(
      (selectedFilterId) => !groupFiltersIds.includes(selectedFilterId),
    );
    setSelectedFilters(newFilters);
  };

  const currency =
    currencyCode || get(window, ['Shopify', 'currency', 'active'], 'USD');
  const format = getCurrencyFormatter(currency);
  const { renderNotifs, addNotif } = useShowPriceUpdatedNotif(
    basePrice,
    isDynamic,
  );
  const { isOpen, handleOnCloseSlidePanel, productInfo, handleOnCardClick } =
    useControlSlidePanel();

  const { updateBundleSetting, bundleSetting } = useBundleSetting();
  const bundleSettingSelected = useMemo(
    () =>
      bundle.Settings.find(({ VariantId }) => VariantId === selectedSetting),
    [bundle.Settings, selectedSetting],
  );

  const {
    selectedProductsBySection,
    addProductToSection,
    removeProductFromSection,
    handleSetSelectedProductsBySection,
  } = useBundleSectionsSelection({
    bundleStep: settings.StepSize,
    addProduct: addProduct,
    removeProduct: removeProduct,
    setSelectedProducts: setSelectedProducts,
    sections: bundle?.Sections as any[],
    productsInSections: bundleData.productsInSections,
    initialSelectedProducts,
  });

  useDeepCompareEffect(() => {
    if (bundleSettingSelected) {
      updateBundleSetting(bundleSettingSelected);
    }
  }, [bundleSettingSelected]);

  useDeepCompareEffect(() => {
    const shopDomain = bundle.PK?.replace(/SHOP#/, '');
    const getBundleSetting = async () => {
      const setting = await getBundleDiscounts({
        bundleId: getIdFromGid(bundle.ProductId),
        variantId: getIdFromGid(variantId),
        shopDomain,
      });

      return setting;
    };

    getBundleSetting().then((setting) => {
      updateBundleSetting(setting);
    });
  }, [bundle.PK, variantId, bundle.ProductId, updateBundleSetting]);

  const dynamicBundleDiscount =
    isDynamic && bundleSetting
      ? pick(bundleSetting, [
          'DynamicDiscountAmount',
          'DynamicDiscountCode',
          'DynamicDiscountId',
          'DynamicDiscountType',
        ])
      : {};

  const handleNext = () => {
    if (hasBabSections) {
      const sectionsWithError = checkSectionsLimits(
        bundleData.productsInSections,
        selectedProductsBySection,
        size,
      );
      if (sectionsWithError.length) {
        sectionsWithError.forEach((sectionWithError) => {
          const { ErrorLabel = '' } = findSectionSettings(
            sectionWithError.settings,
            size,
          );
          const errMessage = `${sectionWithError.name} - ${ErrorLabel}`;
          toast({
            title: errMessage,
            description: '',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        });
        return;
      }
    }
    const itemProperties = getItemProperties();
    let customAttributes = convertPropertiesToAttributes(itemProperties);
    const lineToUpdate = subscription.Lines.find(
      ({ SubscriptionContractLineId }) => SubscriptionContractLineId === lineId,
    );
    const awtMods = lineToUpdate?.CustomAttributes?.find(
      ({ key }) => key === '_awtMods',
    )?.value;
    if (isDynamic) {
      customAttributes = [
        ...customAttributes,
        { key: '_bundleType', value: 'dynamicBaB' },
      ];
    }
    if (awtMods) {
      customAttributes = [
        ...customAttributes,
        { key: '_awtMods', value: awtMods },
      ];
    }
    const newLineProps = {
      customAttributes,
      ...(dynamicVariantId !== variantId && isDynamic
        ? { productVariantId: dynamicVariantId }
        : { productVariantId: bundleSettingSelected?.VariantId }),
    };
    const sellingPlanGid =
      isDynamic && sellingPlan
        ? getGidFromIdForResource(`${sellingPlan}`, 'SellingPlan')
        : undefined;

    updateBundle(
      newLineProps,
      lineId,
      getSelectedProductsWithPropsForCart(),
      sellingPlanGid,
      [dynamicBundleDiscount].filter((d) => !!d.DynamicDiscountId),
      oneTimeAddon,
    );
    closeModal();
  };

  const handleOnUpdateSetting = useCallback(
    (variantId: string) => {
      updateBoxSize(variantId);
    },
    [updateBoxSize],
  );

  const getDiscountedPrice = (price) => {
    const discountedPrice = calculateDiscount(price, discount);

    return price - discountedPrice;
  };

  const discountedBundlePrice =
    discountedPrice || getDiscountedPrice(basePrice);

  useEffect(() => {
    // Check if current variant actually exists, otherwise pre-select the first one on the list
    if (!bundle.Settings.find((i) => i.VariantId === variantId)) {
      const message = t('bundles.new_box_size', {
        newSize: bundle.Settings?.[0].Size,
        newPrice: format(basePrice),
      });
      updateBoxSize(bundle.Settings?.[0].VariantId);

      if (!isDynamic) {
        addNotif(message, { type: 'info' });
      }
    }

    if (productsLength < size) {
      addNotif(t('bundles.bundle_stock_updated'), { type: 'info' });
    }
  }, []);

  const footerStepInfoWithProgressBar = isDynamic ? (
    <div>
      <Span
        modifiers={
          discountedBundlePrice < basePrice ? 'strikethrough' : 'emphasis'
        }
      >
        {format(basePrice)}
      </Span>
      {discountedBundlePrice < basePrice && (
        <Span modifiers={'emphasis'}>{` ${format(
          discountedBundlePrice,
        )}`}</Span>
      )}
      <span>{`${bundlePrice > 0 ? ` + ${format(bundlePrice)}` : ''}`}</span>
    </div>
  ) : null;

  useEffect(() => {
    const sortedByInitiallySelectedProducts = sortProductsBySelected(
      bundleData.productsInCollection,
      initialSelectedProducts,
    );
    setFilteredProducts(sortedByInitiallySelectedProducts);
  }, [bundleData.productsInCollection, initialSelectedProducts]);

  const noTouched = isEqual(
    initialSelectedProducts,
    removeZeros(selectedProducts),
  );

  return (
    <>
      <CreateBundleContainer>
        {renderNotifs()}
        {bundle.HasBuildABoxFiltersActive && (
          <FiltersSection
            setSelectedFilters={setSelectedFilters}
            selectedFilters={selectedFilters}
            handleResetFilters={handleResetFilters}
            filters={bundle.BuildABoxFilterGroups}
          />
        )}
        <main className="bundle-update-content bundle-scrollable-section">
          {hasBabSections ? (
            <CreateBundleWithSections
              bundle={bundle}
              productsInSections={bundleData.productsInSections}
              hideBaBSoldOutOptions={hideBaBSoldOutOptions}
              ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
              sellingPlan={sellingPlan}
              onCardClick={handleOnCardClick}
              canAddProducts={canAddProducts}
              addProduct={addProductToSection}
              removeProduct={removeProductFromSection}
              setSelectedProducts={handleSetSelectedProductsBySection}
              selectedProducts={selectedProducts}
              initialSelectedProducts={initialSelectedProducts}
              selectedProductsBySection={selectedProductsBySection}
              BaBProductsToCartFlag={bundle.Type === 'dynamicBaB'}
              size={size}
              discount={discount}
              filteredProducts={filteredProducts}
            />
          ) : (
            <ProductsHandler
              subscriptionLines={subscription.Lines}
              bundle={bundle}
              productsInCollection={filteredProducts}
              canAddProducts={canAddProducts}
              addProduct={addProduct}
              removeProduct={removeProduct}
              selectedProducts={selectedProducts}
              onCardClick={handleOnCardClick}
              hasSections={bundle.HasSections}
              discount={discount}
              initialSelectedProducts={initialSelectedProducts}
              setSelectedProducts={setSelectedProducts}
              shopConfiguration={shopConfiguration}
              sellingPlan={sellingPlan}
              hideBaBSoldOutOptions={hideBaBSoldOutOptions}
              ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
            />
          )}
        </main>
        {(!isDynamic || (isDynamic && !hasDynamicSize)) && (
          <ProgressBar
            total={size}
            selected={productsLength}
            selectedVariantsId={selectedProducts}
            isBabWithSectionsContent={hasBabSections}
            selectedProductsBySection={selectedProductsBySection}
            removeProductFromSection={removeProductFromSection}
            removeProduct={removeProduct}
            countLabel={`${productsLength} ${t('bundles.of')} ${size}`}
            productsInCollection={bundleData.productsInCollection}
          />
        )}
        <footer>
          <CreateBundleFooter>
            <BundleSelector
              isPrepaid={isPrepaid}
              hasDynamicSize={isDynamic && hasDynamicSize}
              settings={getAllowedBaBSwapVariants()}
              selectedSetting={selectedSetting as string}
              onUpdateSetting={handleOnUpdateSetting}
              originalSettings={bundle.Settings}
            />
            <CreateBundleButtonInfo>
              {footerStepInfoWithProgressBar}
              <span>
                {isDynamic && hasDynamicSize
                  ? t(`bundles.dynamicSize.selection`, {
                      count: productsLength,
                      item: t(
                        `bundles.dynamicSize.item.${
                          productsLength > 1 ? 'plural' : 'singular'
                        }`,
                      ),
                    })
                  : `${productsLength} ${t('bundles.of')} ${size}`}
              </span>
              <Button
                onClick={handleNext}
                filled
                disabled={!isSaveable || noTouched}
                aria-disabled={!isSaveable || noTouched}
              >
                {t('bundles.save')}
              </Button>
            </CreateBundleButtonInfo>
          </CreateBundleFooter>
        </footer>
      </CreateBundleContainer>
      {isOpen && (
        <SlidePanel
          id={productInfo.id}
          handle={productInfo.handle}
          open={isOpen}
          onClose={handleOnCloseSlidePanel}
          imgUrl={productInfo.image}
          imgs={productInfo.images}
          productName={productInfo.title}
          variantName={productInfo.variantTitle}
          subtitle={productInfo.subtitle}
          description={productInfo.description}
          hasOneVariant={productInfo?.hasOneVariant}
          isLoading={!productInfo || isEmpty(productInfo)}
          hidePrice={subscription.ContainsGift}
          reviews={productInfo.reviews}
        />
      )}
    </>
  );
};

const useShowPriceUpdatedNotif = (basePrice: number, isDynamic: boolean) => {
  const { t } = useTranslation();
  const { addNotif, renderNotifs, resetNotifs } = useNotifs();
  const [prevBasePrice, setPreviousBasePrice] = React.useState<number | null>(
    null,
  );
  const currencyCode = get(window, ['Shopify', 'currency', 'active'], 'USD');
  const format = getCurrencyFormatter(currencyCode);

  React.useEffect(() => {
    if (prevBasePrice && basePrice !== prevBasePrice) {
      const message = t('bundles.update_price', {
        prevBasePrice: format(prevBasePrice),
        basePrice: format(basePrice),
      });

      resetNotifs();

      if (!isDynamic) {
        addNotif(message, { type: 'info' });
      }

      setPreviousBasePrice(basePrice);
    } else setPreviousBasePrice(basePrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePrice, prevBasePrice]);

  return { renderNotifs, addNotif };
};

const fetchIndividualProducts = async (
  products: string[],
  result: LiquidProductInCollection[] = [],
): Promise<LiquidProductInCollection[]> => {
  const chunkOfProducts = products.splice(0, MAX_CHUNK_SIZE);

  if (chunkOfProducts.length === 0) {
    return result;
  }

  const productsInCollection = await getBaBSingleProducts(
    chunkOfProducts.join(','),
  );

  return fetchIndividualProducts(products, [
    ...result,
    ...productsInCollection,
  ]);
};

const getIndividualProducts = async (
  productsInfo,
  isVariantCollapsed = false,
): Promise<GlobalVariant[] | GlobalProduct[]> => {
  let singleVariantsCollection: GlobalVariant[] | GlobalProduct[] = [];

  const individualProductsCollection = productsInfo
    ? await fetchIndividualProducts(productsInfo.map(({ Handle }) => Handle))
    : [];

  if (individualProductsCollection && productsInfo?.length) {
    const variants = flatMap(productsInfo, ({ Variants }) => {
      return Variants;
    });

    singleVariantsCollection = !isVariantCollapsed
      ? flatMap(individualProductsCollection, (variantData) =>
          extractVariantAndImage(variantData),
        ).filter((variant) => getOnlySelectedVariants(variant, variants))
      : individualProductsCollection
          .map(({ productData, productMetafields, variantsInventory }) =>
            formatLiquidProductToGlobalProduct(
              {
                ...productData,
                metafields: productMetafields,
              },
              productsInfo,
              variantsInventory,
            ),
          )
          .filter(notEmpty);
  }
  return singleVariantsCollection;
};

export const formatBundleCollections = async (
  {
    productsInCollection = [],
    productsInAddonsCollection,
    productsInfo,
    productsInSections,
    ...bundle
  }: LiquidBundle = {} as LiquidBundle,
  sellingPlan: string,
  variant: string,
  productsInBundle: Record<string, number>,
): Promise<{
  bundle: Bundle;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
  productsInSections?: BabSection[];
}> => {
  const isVariantCollapsed =
    bundle.bundle?.VariantStyle === VariantStyle.collapse;

  const hasSections = bundle?.bundle?.Sections?.length > 0;
  const isCollections = bundle?.bundle?.Sections?.some(
    ({ Collections }) => Collections.length > 0,
  );
  const isSectionsAndCollections = hasSections && isCollections;
  const sectionProductsInCollection = productsInSections?.flatMap(
    ({ individualProductsCollection, productsInCollections }) =>
      isSectionsAndCollections
        ? productsInCollections.flat()
        : individualProductsCollection,
  );

  const sectionProductsInfo = productsInSections?.flatMap(({ products }) => {
    return products;
  });

  const productsCollection = hasSections
    ? sectionProductsInCollection
    : productsInCollection;

  const productsInformation = hasSections ? sectionProductsInfo : productsInfo;

  const singleVariantsCollection = productsInformation?.length
    ? await getIndividualProducts(productsInformation, isVariantCollapsed)
    : [];

  const variantsCollections: GlobalVariant[] | GlobalProduct[] =
    !isVariantCollapsed
      ? (flatMap(productsCollection, (productData) =>
          extractVariantAndImage(productData),
        ) as GlobalVariant[])
      : productsCollection
          .map(({ productData, productMetafields, variantsInventory }) =>
            formatLiquidProductToGlobalProduct(
              {
                ...productData,
                metafields: productMetafields,
              },
              productsInformation,
              variantsInventory,
            ),
          )
          .filter(notEmpty);

  const addonsCollections = !isVariantCollapsed
    ? flatMap(productsInAddonsCollection, (addonData) =>
        extractVariantAndImage(addonData, true),
      )
    : productsInAddonsCollection
        .map(({ productData, productMetafields, variantsInventory }) =>
          formatLiquidProductToGlobalProduct(
            {
              ...productData,
              metafields: productMetafields,
            },
            [],
            variantsInventory,
          ),
        )
        .filter(notEmpty);

  let collection =
    singleVariantsCollection.length > 0
      ? singleVariantsCollection
      : variantsCollections;

  if (isGlobalProductList(collection)) {
    collection = splitProductInVariants(collection, productsInBundle);
  }

  const bundleFormat = {
    ...bundle.bundle,
    SellingPlan: sellingPlan,
    VariantId: variant,
  };

  return {
    ...bundle,
    productsInCollection: collection,
    productsInAddonsCollection: addonsCollections,
    productsInSections: productsInSections,
    bundle: bundleFormat,
  };
};

interface Attribute {
  key: string;
  value: string;
}

const convertPropertiesToAttributes = (
  itemProperties: BundleCartProperties,
): Attribute[] => {
  const newCustomProperties = Object.entries(itemProperties).reduce(
    (acc, [key, value]) => {
      const attribute = {
        key,
        value: value.toString(),
      };
      return [...acc, attribute];
    },
    [] as Attribute[],
  );

  return newCustomProperties;
};

export const sortProductsBySelected = (products = [], selected = {}) => {
  const sortedProducts = products.reduce((acc: GlobalVariant[], element) => {
    if (
      Object.keys(selected).includes(element.id.toString()) ||
      element?.variants?.some((variant) =>
        Object.keys(selected).includes(variant.id?.toString()),
      )
    ) {
      return [element, ...acc];
    }
    return [...acc, element];
  }, []);
  return sortedProducts;
};

function removeZeros(obj2: Record<string, number>) {
  return Object.entries(obj2).reduce(
    (acc, [key, value]: [string, number]) => {
      if (value === null || value === undefined || value === 0) {
        return acc;
      }
      return { ...acc, [key]: value };
    },
    {} as Record<string, number>,
  );
}
